/* The metadata copilot, built to the reference design.
 *
 * **Two rules shape this screen, and they are the reference's own.**
 *
 * *Status decides whether `text` is an answer.* Every outcome is a 201 carrying prose and only
 * `answered` is prose a model wrote and a validator accepted; the other four are host-written
 * fallback. Rendering those as the copilot's reply would present the platform's own words as a
 * model's. So presentation derives from status in one place — three presentations rather than two,
 * because a fault is ours and should be escalated where an outage should not.
 *
 * *A grounded claim is worth nothing if the reader cannot reach the record behind it.* The answer
 * and its receipts sit in one frame, side by side, and selecting either highlights the other.
 * Citations as a footnote list at the bottom put the burden of matching claim to source on the
 * reader — which is what the previous version did.
 *
 * **What the redesign changed, and what it deliberately did not.** Every API call, hook, query key
 * and guard is the one that was here before: `useAsk`, `useTranscript`, `useCapabilities`, the fresh
 * idempotency key per ask, the generated response guards. The bounds are still read from
 * `/capabilities` rather than hard-coded — `max_question_chars`, `max_snippets`, `rung`,
 * `mutating_tools`, `retrievable`, `never_retrieved` — so a deployment that changes one changes the
 * screen.
 *
 * **One thing the reference asserts that this platform's API cannot, stated rather than faked.**
 * The reference authors its prose as segments with a numbered marker after each claim. The API
 * returns one `text` string plus a flat `citations` list, so there is no segment data — and
 * inventing one would put markers against claims nobody tied to a record. `segmentsFrom` numbers
 * the citation tokens the model actually wrote into its answer; where there are none, the prose
 * renders plain and the receipts stay independently selectable.
 */

import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useAsk, useCapabilities, useTranscript } from '../api/copilot';
import {
  STATUS_LABEL,
  STATUS_TONE,
  isModelAuthored,
  noReceiptsNote,
  presentationFor,
  provenanceOf,
  receiptsFrom,
  refusalSummary,
  segmentsFrom,
} from '../model/copilot';

import type { Asked, StoredAnswer } from '../api/copilot';
import type { AnswerStatus } from '@/shared/api/generated/ai-copilot';

/** The fallback question cap, used only until `/capabilities` answers.
 *
 * The deployment's own number is the authority — a client that hard-coded it would keep refusing at
 * 500 the day an operator raised the limit. This exists so the counter renders something during the
 * first paint rather than `n / undefined`.
 */
const FALLBACK_MAX_CHARS = 500;

/** The three starters the reference offers.
 *
 * Static copy rather than an API read, and that is honest: there is no suggestions endpoint on the
 * copilot surface, these are examples of what the retrievable kinds can answer, and inventing a
 * backend to serve three strings would be the opposite of a minimum change. They fill the composer
 * rather than submitting, so nobody spends a completion by brushing one.
 */
const STARTER_KEYS = [
  'copilot.starter.profiled',
  'copilot.starter.failing',
  'copilot.starter.contracts',
] as const;

/** An instant, in the reader's own locale.
 *
 * The API sends ISO-8601 because the server does not know the reader's locale — so the formatting
 * decision belongs here. One helper rather than two call sites, because the answer header and the
 * history row showing the same instant differently is the kind of difference nobody notices and
 * everybody trips over.
 *
 * A malformed value renders as itself rather than as "Invalid Date": the string came from the
 * platform, and showing it is more useful than hiding it behind a formatter's failure.
 */
function askedAtLabel(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleString();
}

/** Everything the answer pane needs, from either shape the API returns. */
interface Displayable {
  readonly id: string;
  readonly question: string;
  readonly status: AnswerStatus;
  readonly text: string;
  readonly citations: readonly string[];
  readonly refused_kinds: readonly string[];
  readonly truncated: boolean;
  readonly validator?: string | null | undefined;
  readonly rejection_reason?: string | null | undefined;
  /** Present on a fresh ask only — the transcript route does not carry it. */
  readonly snippet_count?: number | undefined;
  /** From the stored row. `undefined` on a fresh ask, where "Just now" is the honest label. */
  readonly asked_at?: string | undefined;
}

function fromAsked(asked: Asked, question: string): Displayable {
  return { ...asked, id: 'asked', question };
}

function fromStored(stored: StoredAnswer): Displayable {
  return { ...stored };
}

/** The status chip: a ruled square and the label the reference gives the status. */
function StatusChip({ status }: { readonly status: AnswerStatus }): ReactElement {
  const tone = STATUS_TONE[status];
  return (
    <span className="lq-cp__chip" style={{ color: tone.color, background: tone.chip }}>
      <span
        className="lq-cp__dot"
        style={{ background: tone.dot, boxShadow: `inset 0 0 0 1.5px ${tone.color}` }}
      />
      {STATUS_LABEL[status]}
    </span>
  );
}

/** The answer and its receipts, in one frame. */
function AnswerPane({
  answer,
  maxSnippets,
}: {
  readonly answer: Displayable;
  readonly maxSnippets: number;
}): ReactElement {
  const { t } = useTranslation();
  /* Which receipt is selected. Held here rather than in the page, so switching questions in the
     history resets it by remounting — a citation number that survived a change of question would
     point at a record from the previous answer. */
  const [cited, setCited] = useState<number | null>(null);
  useEffect(() => {
    setCited(null);
  }, [answer.id]);

  const presentation = presentationFor(answer.status);
  const modelAuthored = isModelAuthored(answer.status);
  const receipts = receiptsFrom(answer.citations);
  const segments = modelAuthored ? segmentsFrom(answer.text, receipts) : [];
  const refused = refusalSummary(answer.refused_kinds);

  return (
    <div className="lq-cp__answer">
      <div
        className={`lq-cp__body${
          presentation === 'answer'
            ? ' lq-cp__body--answer'
            : presentation === 'fault'
              ? ' lq-cp__body--fault'
              : ''
        }`}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <StatusChip status={answer.status} />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 'var(--text-xs)',
              color: 'var(--text-muted)',
            }}
          >
            {answer.asked_at === undefined
              ? t('copilot.justNow')
              : askedAtLabel(answer.asked_at)}
          </span>
        </div>

        <div className="lq-cp__question">{answer.question}</div>

        <div
          className={`lq-cp__prose${modelAuthored ? '' : ' lq-cp__prose--notice'}`}
          style={{
            color: modelAuthored ? 'var(--text-primary)' : 'var(--text-secondary)',
          }}
        >
          {modelAuthored ? (
            segments.map((segment, index) => (
              /* Keyed on position and marker together. A segment has no identity of its own — it is a
                 slice of one string, and two slices can be identical — so the position is what makes it
                 unique within this one answer, which is all React needs. */
              <span key={`${String(index)}:${String(segment.marker)}`}>
                {segment.text}
                {segment.marker !== null && (
                  <button
                    type="button"
                    className={`lq-cp__marker${cited === segment.marker ? ' lq-cp__marker--on' : ''}`}
                    onClick={() => {
                      setCited(cited === segment.marker ? null : segment.marker);
                    }}
                  >
                    {segment.marker}
                  </button>
                )}
              </span>
            ))
          ) : (
            /* Host-written prose, presented as the platform speaking — never as an answer. */
            <>{answer.text}</>
          )}
        </div>

        <div className="lq-cp__prov">
          <span
            className="lq-cp__provMark"
            style={{
              background:
                presentation === 'answer'
                  ? 'var(--accent)'
                  : presentation === 'fault'
                    ? 'var(--status-error)'
                    : 'var(--text-muted)',
            }}
          />
          <span className="lq-cp__provText">
            {provenanceOf(answer.status, answer.validator)}
          </span>
        </div>

        {answer.status === 'refused' &&
          answer.rejection_reason !== null &&
          answer.rejection_reason !== undefined &&
          answer.rejection_reason !== '' && (
            <div className="lq-cp__note" style={{ color: 'var(--status-warn)' }}>
              {t('copilot.rejectionReason', { reason: answer.rejection_reason })}
            </div>
          )}

        {presentation === 'fault' && (
          <div className="lq-cp__note" style={{ color: 'var(--status-error)' }}>
            {t('copilot.faultHint')}
          </div>
        )}

        {/* **A deliberate permissions disclosure**, and the sentence that stops a narrowed grant
            from looking like a broken copilot: an answer thin because of permissions and one thin
            because nothing matched are different facts, and only this field separates them. */}
        {refused !== '' && (
          <div className="lq-band lq-band--warn" style={{ marginTop: 14 }}>
            <div className="lq-band__body">
              {t('copilot.refusedBody', { kinds: refused })}
            </div>
          </div>
        )}
      </div>

      <div className="lq-cp__rail">
        <div className="lq-cp__railHead">
          <span className="lq-cp__eyebrow" style={{ flex: 1 }}>
            {t('copilot.receipts')}
          </span>
          {/* Only a fresh ask reports how many snippets were used; the transcript row does not
              store it, so the count is omitted rather than shown as zero. */}
          {answer.snippet_count !== undefined && (
            <span
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              {t('copilot.snippetsOf', { used: answer.snippet_count, max: maxSnippets })}
            </span>
          )}
        </div>
        <div className="lq-cp__rule lq-cp__rule--rail" />

        {receipts.length > 0 ? (
          <>
            {receipts.map((receipt) => (
              <button
                type="button"
                key={`${receipt.kind}:${receipt.ref}`}
                className={`lq-cp__receipt${cited === receipt.n ? ' lq-cp__receipt--on' : ''}`}
                onClick={() => {
                  setCited(cited === receipt.n ? null : receipt.n);
                }}
              >
                <div className="lq-cp__receiptHead">
                  <span className="lq-cp__receiptNum">{receipt.n}</span>
                  <span className="lq-cp__eyebrow" style={{ flex: 1 }}>
                    {receipt.kind === '' ? t('copilot.receiptUnkinded') : receipt.kind}
                  </span>
                </div>
                <div className="lq-cp__receiptRef">{receipt.ref}</div>
              </button>
            ))}
            <div
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginTop: 10,
              }}
            >
              {t('copilot.receiptsNote')}
            </div>
          </>
        ) : (
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            {noReceiptsNote(answer.status)}
          </div>
        )}

        {answer.truncated && (
          <div
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--status-warn)',
              marginTop: 12,
              paddingTop: 10,
              borderTop: '1px solid var(--border-subtle)',
            }}
          >
            {t('copilot.truncatedNote')}
          </div>
        )}
      </div>
    </div>
  );
}

/** The two panels stating the boundary, read from `/capabilities`. */
function BoundaryPanels(): ReactElement | null {
  const { t } = useTranslation();
  const { data, isPending, isError } = useCapabilities();

  if (isPending) return <SkeletonRows rows={4} />;
  /* A failed capabilities read is reported rather than rendered as an empty boundary: a panel
     saying nothing is retrievable would be a claim about this deployment's permissions. */
  if (isError) {
    return (
      <Alert tone="warning" title={t('copilot.boundaryFailed')}>
        {t('copilot.boundaryFailedBody')}
      </Alert>
    );
  }

  return (
    <>
      <div className="lq-cp__panel" style={{ marginBottom: 16 }}>
        <div className="lq-cp__eyebrow" style={{ marginBottom: 10 }}>
          {t('copilot.readsGovernedBy')}
        </div>
        {data.retrievable.map((entry) => (
          <div className="lq-cp__kindRow" key={entry.kind}>
            <span className="lq-cp__kindName">{entry.kind}</span>
            <span
              style={{
                flex: 1,
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                overflowWrap: 'anywhere',
              }}
            >
              {entry.governed_by}
            </span>
          </div>
        ))}
        {/* The deployment's own sentence, not a paraphrase: the granularity claim is the one a
            reader is most likely to over-read, so it is shown in the platform's words. */}
        <div
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--text-muted)',
            marginTop: 10,
          }}
        >
          {data.authorization_policy}
        </div>
      </div>

      <div className="lq-cp__panel">
        <div className="lq-cp__eyebrow" style={{ marginBottom: 10 }}>
          {t('copilot.neverReaches')}
        </div>
        {data.never_retrieved.map((entry) => (
          <div className="lq-cp__neverRow" key={entry}>
            <span className="lq-cp__neverMark" />
            <span style={{ flex: 1, color: 'var(--text-secondary)' }}>{entry}</span>
          </div>
        ))}
        <div
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--text-muted)',
            marginTop: 10,
          }}
        >
          {data.mutating_tools}
        </div>
      </div>
    </>
  );
}

export function CopilotConsole(): ReactElement {
  const { t } = useTranslation();
  const [question, setQuestion] = useState('');
  /* Which transcript row is open. `null` means "the freshest thing there is", which is the ask's
     own answer when one exists and otherwise the newest stored row. */
  const [selected, setSelected] = useState<string | null>(null);

  const ask = useAsk();
  const transcript = useTranscript();
  const capabilities = useCapabilities();

  const maxChars = capabilities.data?.max_question_chars ?? FALLBACK_MAX_CHARS;
  const maxSnippets = capabilities.data?.max_snippets ?? 0;
  const over = question.length > maxChars;
  const valid = question.trim() !== '' && !over;

  const stored = transcript.data ?? [];
  const asked = ask.data === undefined ? null : fromAsked(ask.data, ask.variables);

  /* The freshest answer wins when nothing is selected, so asking a question shows its answer
     without a click. Selecting a history row then pins that one. */
  const shown: Displayable | null =
    selected === null
      ? (asked ?? (stored[0] === undefined ? null : fromStored(stored[0])))
      : (() => {
          const match = stored.find((row) => row.id === selected);
          return match === undefined ? null : fromStored(match);
        })();

  /* Nothing to show *and* no grounds for saying so. */
  const unreadable = shown === null && transcript.isError;

  const submit = (): void => {
    if (!valid) return;
    /* Cleared here and the selection reset, so the answer that arrives is the one on screen. */
    ask.mutate(question.trim());
    setQuestion('');
    setSelected(null);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 20,
          flexWrap: 'wrap',
          marginBottom: 18,
        }}
      >
        <div style={{ flex: 1, minWidth: 280 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 27,
              fontWeight: 800,
              letterSpacing: '-.02em',
            }}
          >
            {t('copilot.title')}
          </h1>
          <p className="lq-cp__lede">{t('copilot.subtitle')}</p>
        </div>
        {/* Rung and tools, from the deployment rather than from copy: both are claims about what
            this install can do, and a hard-coded "1" would keep saying so if it changed. */}
        {capabilities.data !== undefined && (
          <div className="lq-cp__headStats">
            <div>
              <div className="lq-cp__eyebrow" style={{ marginBottom: 4 }}>
                {t('copilot.rungLabel')}
              </div>
              <div className="lq-cp__headValue">
                {t('copilot.rungValue', { rung: capabilities.data.rung })}
              </div>
            </div>
            <div>
              <div className="lq-cp__eyebrow" style={{ marginBottom: 4 }}>
                {t('copilot.toolsLabel')}
              </div>
              <div className="lq-cp__headValue">{t('copilot.toolsValue')}</div>
            </div>
          </div>
        )}
      </div>
      <div className="lq-cp__rule lq-cp__rule--head" />

      <div className="lq-cp__ask">
        <div className="lq-cp__askRow">
          <span className="lq-cp__glyph" aria-hidden="true">
            ?
          </span>
          <textarea
            className="lq-cp__input"
            rows={2}
            value={question}
            aria-label={t('copilot.questionLabel')}
            placeholder={t('copilot.questionPlaceholder')}
            onChange={(event) => {
              setQuestion(event.target.value);
            }}
          />
          <button
            type="button"
            className="lq-cp__send"
            disabled={!valid || ask.isPending}
            onClick={submit}
          >
            {ask.isPending ? t('copilot.asking') : t('copilot.ask')}
          </button>
        </div>
        <div className="lq-cp__starters">
          <span className="lq-cp__eyebrow">{t('copilot.try')}</span>
          {STARTER_KEYS.map((key) => (
            <button
              type="button"
              key={key}
              className="lq-cp__starter"
              onClick={() => {
                setQuestion(t(key));
              }}
            >
              {t(key)}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <span
            className="lq-cp__count"
            style={{ color: over ? 'var(--status-error)' : 'var(--text-muted)' }}
          >
            {t('copilot.charCount', { used: question.length, max: maxChars })}
          </span>
        </div>
        {/* **Refused whole rather than trimmed**, which is what the server does — so the screen
            says so instead of silently cutting the question the person wrote. */}
        {over && (
          <div className="lq-cp__over">
            {t('copilot.overBy', { over: question.length - maxChars })}
          </div>
        )}
      </div>

      {ask.isError && (
        <Alert tone="error" title={t('copilot.askFailed')}>
          {ask.error.message}
        </Alert>
      )}

      {/* **An absence is only claimed when it was actually read.** `unreadable` exists so a failed
          transcript fetch does not fall through to the empty state: "Nothing asked yet" would be a
          claim about this tenant's history that a failed request has no grounds for -- the anomaly
          screen's every-row-says-absent defect, one screen over. The failure is reported in the
          history panel below, so the pane stays silent rather than asserting anything. */}
      {transcript.isPending && ask.data === undefined ? (
        <SkeletonRows rows={5} />
      ) : unreadable ? null : shown === null ? (
        <EmptyState
          title={t('copilot.noQuestions')}
          guidance={t('copilot.noQuestionsGuidance')}
        />
      ) : (
        <AnswerPane answer={shown} maxSnippets={maxSnippets} />
      )}

      <div className="lq-cp__split">
        <div className="lq-cp__splitMain">
          <div className="lq-cp__sectionHead">
            <h3>{t('copilot.earlier')}</h3>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
              {t('copilot.historyCount', { count: stored.length })}
            </span>
          </div>
          {transcript.isError ? (
            <Alert tone="warning" title={t('copilot.historyFailed')}>
              {t('copilot.historyFailedBody')}
            </Alert>
          ) : stored.length === 0 ? (
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
              {t('copilot.noHistory')}
            </div>
          ) : (
            <>
              {stored.map((row) => {
                const tone = STATUS_TONE[row.status];
                const on = shown !== null && shown.id === row.id;
                return (
                  <button
                    type="button"
                    key={row.id}
                    className={`lq-cp__historyRow${on ? ' lq-cp__historyRow--on' : ''}`}
                    onClick={() => {
                      setSelected(row.id);
                    }}
                  >
                    <span
                      className="lq-cp__dot"
                      style={{
                        width: 9,
                        height: 9,
                        background: tone.dot,
                        boxShadow: `inset 0 0 0 1.5px ${tone.color}`,
                      }}
                    />
                    <span className="lq-cp__historyQ">{row.question}</span>
                    <span
                      style={{
                        flex: 'none',
                        fontSize: 'var(--text-xs)',
                        color: tone.color,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {STATUS_LABEL[row.status]}
                    </span>
                    {/* Always present on a stored row: the column has a default and the
                        contract declares it required. */}
                    <span
                      style={{
                        flex: 'none',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-muted)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {askedAtLabel(row.asked_at)}
                    </span>
                  </button>
                );
              })}
              <div
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-muted)',
                  marginTop: 10,
                }}
              >
                {t('copilot.historyNote')}
              </div>
            </>
          )}
        </div>

        <div className="lq-cp__splitAside">
          <BoundaryPanels />
        </div>
      </div>
    </div>
  );
}
